import { CartLinesUpdateEvent } from '@shopify/events';

class LookbookCart04 extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.bound = true;
    this.form.id = `lookbook-cart-${crypto.randomUUID()}`;
    this.select = this.form.elements.id;
    this.quantity = this.form.elements.quantity;
    this.button = this.form.querySelector('.l04-add');
    this.status = this.form.querySelector('.l04-status');
    this.viewCart = this.form.querySelector('.l04-view-cart');
    this.buttonLabel = this.button.textContent;
    this.select.addEventListener('change', () => this.updateVariant());
    this.form.addEventListener('submit', event => this.add(event));
    this.updateVariant();
  }

  updateVariant() {
    const option = this.select.selectedOptions[0];
    const valid = Boolean(option?.value && !option.disabled && !this.select.disabled);
    this.button.disabled = this.busy || this.locked || !valid;
    if (!valid) return;
    this.querySelector('[data-l04-price]').textContent = option.dataset.price;
    this.quantity.min = option.dataset.min || '1';
    this.quantity.step = option.dataset.step || '1';
    if (option.dataset.max) this.quantity.max = option.dataset.max;
    else this.quantity.removeAttribute('max');
    this.quantity.value = this.quantity.min;
  }

  async add(event) {
    event.preventDefault();
    if (this.busy || this.locked || !this.form.reportValidity()) return;
    const option = this.select.selectedOptions[0];
    if (!option?.value || option.disabled || this.select.disabled) return;
    const id = option.value;
    const quantity = Number(this.quantity.value);
    this.busy = true;
    this.button.disabled = true;
    this.button.textContent = this.dataset.adding;
    this.status.textContent = '';
    this.viewCart.hidden = true;
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity }], promise: deferred.promise }));
    let confirmed = false;
    let rejected = false;
    let partial = false;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }], sections, sections_url: `${location.pathname}${location.search || ''}` })
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        rejected = response.status >= 400 && response.status < 500;
        partial = response.status === 422;
        throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      }
      confirmed = true;
      this.status.textContent = this.dataset.added;
      this.viewCart.hidden = false;
      this.dispatchEvent(new CustomEvent('lookbook:added', { bubbles: true, detail: { productId: this.dataset.productId, variantId: id, quantity } }));
      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.dataset.refreshError);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: result.sections, source: 'product-form-component', sourceId: this.form.id, itemCount: quantity, productId: this.dataset.productId, didError: false } });
    } catch (error) {
      // A 422 can still add the remaining inventory. Reconcile the real cart
      // while marking the event as an error so the drawer does not auto-open.
      if (partial) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
          if (!cartResponse.ok) throw new Error(this.dataset.refreshError);
          const cart = await cartResponse.json();
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: this.form.id, itemCount: quantity, productId: this.dataset.productId, didError: true } });
        } catch (refreshError) {
          deferred.reject(refreshError);
        }
      } else deferred.reject(error);
      this.locked = confirmed || !rejected || partial;
      this.status.textContent = confirmed ? this.dataset.refreshError : rejected ? error.message || this.dataset.error : this.dataset.uncertain;
      this.viewCart.hidden = !this.locked;
    } finally {
      this.busy = false;
      this.button.textContent = this.buttonLabel;
      this.button.disabled = this.locked || this.select.disabled || !this.select.value || this.select.selectedOptions[0]?.disabled;
    }
  }
}

if (!customElements.get('lookbook-cart-04')) customElements.define('lookbook-cart-04', LookbookCart04);
